import { useMemo, useState } from 'react'
import {
  Button,
  DataTable,
  DateRangeChip,
  FilterBar,
  Money,
  MultiSelectChip,
  SelectChip,
  SummaryBanner,
  SyncStatus,
  type ColumnDef,
} from '../components'
import { useMe } from '../lib/use-me'
import { useOrganizations } from '../lib/api/organizations'
import { useCounterparties } from '../lib/api/counterparties'
import {
  flattenInvoices,
  useBootstrapInvoices,
  useInvoiceReceivableSummary,
  useInvoices,
} from '../lib/api/invoices'
import type { Currency, Invoice, InvoiceReceivableSummary, InvoiceStatus } from '../lib/api/types'
import { fmtMoney } from '../lib/money'
import { classifyDuePeriod, type DuePeriod } from '../lib/due-period'
import { isUnposted, unpostedAmount } from '../lib/invoice-status'
import { InvoicePaymentDrawer } from './invoice-payment-drawer'

const STATUS_ITEMS: { id: InvoiceStatus; name: string }[] = [
  { id: 'issued', name: 'Виставлено' },
  { id: 'partial', name: 'Частково оплачено' },
  { id: 'paid', name: 'Оплачено' },
  { id: 'cancelled', name: 'Скасовано' },
]

const STATUS_TONE: Record<InvoiceStatus, { label: string; tone: string }> = {
  issued: { label: 'Виставлено', tone: 'info' },
  partial: { label: 'Частково', tone: 'warning' },
  paid: { label: 'Оплачено', tone: 'success' },
  cancelled: { label: 'Скасовано', tone: '' },
}

function InvoiceStatusChip({ status, overdue }: { status: InvoiceStatus; overdue: boolean }) {
  if (overdue && (status === 'issued' || status === 'partial')) {
    return (
      <span className="chip danger">
        <span className="chip-dot" />
        Прострочено
      </span>
    )
  }
  const { label, tone } = STATUS_TONE[status]
  return (
    <span className={`chip ${tone}`}>
      {tone && <span className="chip-dot" />}
      {label}
    </span>
  )
}

export function InvoicesPage() {
  const me = useMe()
  const isAdmin = me.data?.role === 'admin'
  const [orgIds, setOrgIds] = useState<string[]>([])
  const [status, setStatus] = useState<InvoiceStatus | null>(null)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  // Due-date filter driven by clicking a chip in the receivable banner.
  const [duePeriod, setDuePeriod] = useState<DuePeriod | null>(null)
  const [search, setSearch] = useState('')
  const [payInvoice, setPayInvoice] = useState<Invoice | null>(null)
  const [onlyUnposted, setOnlyUnposted] = useState(false)

  const organizations = useOrganizations()
  const counterparties = useCounterparties()
  const counterpartyMap = useMemo(
    () => new Map((counterparties.data ?? []).map((c) => [c.id, c.name])),
    [counterparties.data],
  )
  const organizationMap = useMemo(
    () => new Map((organizations.data ?? []).map((o) => [o.id, o.name])),
    [organizations.data],
  )

  const list = useInvoices({
    organizationId: orgIds.length === 1 ? orgIds[0] : undefined,
    status: status ?? undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  })

  const today = new Date().toISOString().slice(0, 10)

  const invoices = useMemo(() => {
    let filtered = flattenInvoices(list.data)
    if (orgIds.length > 1) filtered = filtered.filter((i) => orgIds.includes(i.organizationId))
    // Banner period chip: keep only the outstanding receivables that fall in the
    // clicked due-date window, matching exactly the figure behind the chip.
    if (duePeriod != null) {
      filtered = filtered.filter((i) => invoiceDuePeriod(i, today) === duePeriod)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      filtered = filtered.filter(
        (i) =>
          i.number.toLowerCase().includes(q) ||
          i.bitrixInvoiceId.toLowerCase().includes(q) ||
          (i.payerName?.toLowerCase().includes(q) ?? false),
      )
    }
    if (onlyUnposted) filtered = filtered.filter(isUnposted)
    return filtered
  }, [list.data, orgIds, duePeriod, today, search, onlyUnposted])

  const unpostedCount = useMemo(
    () => flattenInvoices(list.data).filter(isUnposted).length,
    [list.data],
  )

  // Toggle a due-date window from the banner. The banner figures only count
  // outstanding (issued/partial) receivables, so a paid/cancelled status filter
  // would empty the table — drop it back to "all" when narrowing by period.
  const handlePeriodToggle = (period: DuePeriod) => {
    if (duePeriod === period) {
      setDuePeriod(null)
      return
    }
    setDuePeriod(period)
    if (status === 'paid' || status === 'cancelled') setStatus(null)
  }

  const bootstrap = useBootstrapInvoices()
  const orgItems = (organizations.data ?? []).map((o) => ({ id: o.id, name: o.name }))
  const hasFilters =
    orgIds.length > 0 ||
    status != null ||
    !!dateFrom ||
    !!dateTo ||
    duePeriod != null ||
    !!search.trim() ||
    onlyUnposted

  const receivable = useInvoiceReceivableSummary({
    organizationId: orgIds.length === 1 ? orgIds[0] : undefined,
  })

  const columns: ColumnDef<Invoice>[] = [
    {
      id: 'number',
      header: '№',
      menuLabel: 'Номер',
      width: 110,
      sortValue: (inv) => inv.number,
      cell: (inv) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>
          {inv.number}
        </span>
      ),
    },
    {
      id: 'organization',
      header: 'Наша компанія',
      width: 150,
      cellClassName: 'fg2',
      sortValue: (inv) => organizationMap.get(inv.organizationId) ?? null,
      cell: (inv) => organizationMap.get(inv.organizationId) ?? <span className="fg3">—</span>,
    },
    {
      id: 'counterparty',
      header: 'Клієнт',
      width: 200,
      cellClassName: 'fg2',
      sortValue: (inv) => counterpartyMap.get(inv.counterpartyId) ?? null,
      cell: (inv) => counterpartyMap.get(inv.counterpartyId) ?? <span className="fg3">—</span>,
    },
    {
      id: 'payer',
      header: 'Платник',
      width: 200,
      cellClassName: 'fg2',
      sortValue: (inv) => inv.payerName,
      cell: (inv) =>
        inv.payerName ? (
          <span
            title={inv.payerName}
            style={{
              display: 'block',
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {inv.payerName}
          </span>
        ) : (
          <span className="fg3">—</span>
        ),
    },
    {
      id: 'items',
      header: 'Призначення',
      width: 240,
      cellClassName: 'fg3',
      sortValue: (inv) => inv.itemsSummary,
      cell: (inv) =>
        inv.itemsSummary ? (
          <span
            title={inv.itemsSummary}
            style={{
              display: 'block',
              maxWidth: 240,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {inv.itemsSummary}
          </span>
        ) : (
          <span className="fg3">—</span>
        ),
    },
    {
      id: 'issueDate',
      header: 'Виставлено',
      width: 110,
      cellClassName: 'fg3 num',
      sortValue: (inv) => inv.issueDate,
      cell: (inv) => inv.issueDate,
    },
    {
      id: 'expectedPaymentDate',
      header: 'Очікувана оплата',
      width: 130,
      cellClassName: 'num',
      sortValue: (inv) => inv.expectedPaymentDate,
      cell: (inv) =>
        inv.expectedPaymentDate ? (
          <span style={{ color: isOverdue(inv, today) ? 'var(--danger-fg)' : 'var(--fg-2)' }}>
            {inv.expectedPaymentDate}
          </span>
        ) : (
          <span className="fg3">—</span>
        ),
    },
    {
      id: 'totalAmount',
      header: 'Сума',
      width: 120,
      thClassName: 'num',
      cellClassName: 'num',
      sortValue: (inv) => inv.totalAmount,
      cell: (inv) => <Money amount={inv.totalAmount} currency={inv.currency} />,
    },
    {
      id: 'paidAmount',
      header: 'Оплачено',
      width: 160,
      sortValue: (inv) => inv.paidAmount,
      cell: (inv) => (
        <div>
          <ProgressMini
            value={inv.paidAmount}
            total={inv.totalAmount}
            currency={inv.currency}
            pct={
              inv.totalAmount > 0
                ? Math.min(100, Math.round((inv.paidAmount / inv.totalAmount) * 100))
                : 0
            }
          />
          {isUnposted(inv) && (
            <div style={{ fontSize: 11, color: 'var(--danger-fg)', marginTop: 2 }}>
              ⚠ не проведено: {fmtMoney(unpostedAmount(inv), inv.currency)}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Статус',
      width: 120,
      sortValue: (inv) => inv.status,
      cell: (inv) => <InvoiceStatusChip status={inv.status} overdue={isOverdue(inv, today)} />,
    },
    {
      id: 'actions',
      header: '',
      width: 110,
      pinned: true,
      cell: (inv) => {
        const unposted = isUnposted(inv)
        const canAct = inv.status === 'issued' || inv.status === 'partial' || unposted
        if (!canAct) return null
        return (
          <div style={{ textAlign: 'right' }}>
            <Button variant="secondary" size="sm" icon="check" onClick={() => setPayInvoice(inv)}>
              {unposted && inv.status === 'paid' ? 'Провести' : 'Оплатити'}
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <FilterBar
        right={
          isAdmin ? (
            <Button
              variant="secondary"
              size="sm"
              icon="refresh-cw"
              onClick={() => bootstrap.mutate()}
              disabled={bootstrap.isPending}
            >
              {bootstrap.isPending ? 'Синхронізація…' : 'Синхронізувати з B24'}
            </Button>
          ) : undefined
        }
      >
        <MultiSelectChip
          label="Організації"
          items={orgItems}
          selectedIds={orgIds}
          onChange={setOrgIds}
        />
        <SelectChip
          label="Статус"
          items={STATUS_ITEMS}
          selectedId={status}
          onChange={(id) => setStatus(id as InvoiceStatus | null)}
        />
        <DateRangeChip
          from={dateFrom}
          to={dateTo}
          clearable
          onChange={(f, t) => {
            setDateFrom(f)
            setDateTo(t)
          }}
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Пошук за номером / платником…"
          className="input"
          style={{ maxWidth: 220 }}
        />
        {unpostedCount > 0 && (
          <button
            type="button"
            className={`chip ${onlyUnposted ? 'danger' : ''}`}
            aria-pressed={onlyUnposted}
            onClick={() => setOnlyUnposted((v) => !v)}
          >
            <span className="chip-dot" />
            Потребують проведення ({unpostedCount})
          </button>
        )}
      </FilterBar>

      <div className="content">
        <SyncStatus
          isPending={bootstrap.isPending}
          isError={bootstrap.isError}
          error={bootstrap.error}
          data={bootstrap.data}
        />
        {list.isLoading && <SkeletonRows />}
        {!list.isLoading && invoices.length === 0 && (
          <EmptyStateLocal
            isAdmin={isAdmin}
            isFiltered={hasFilters}
            onSync={() => bootstrap.mutate()}
            onReset={() => {
              setOrgIds([])
              setStatus(null)
              setDateFrom('')
              setDateTo('')
              setDuePeriod(null)
              setSearch('')
              setOnlyUnposted(false)
            }}
            bootstrapPending={bootstrap.isPending}
          />
        )}
        {receivable.data && receivable.data.count > 0 && (
          <ReceivableBanner
            summary={receivable.data}
            scopedToOrg={orgIds.length === 1}
            activePeriod={duePeriod}
            onPeriodToggle={handlePeriodToggle}
          />
        )}
        {!list.isLoading && invoices.length > 0 && (
          <div className="card">
            <DataTable
              tableKey="invoices"
              columns={columns}
              rows={invoices}
              rowKey={(inv) => inv.id}
            />
            {list.hasNextPage && (
              <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? 'Завантаження…' : 'Завантажити ще'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {payInvoice && (
        <InvoicePaymentDrawer
          invoice={payInvoice}
          counterpartyName={counterpartyMap.get(payInvoice.counterpartyId)}
          onClose={() => setPayInvoice(null)}
        />
      )}
    </>
  )
}

// The banner figures only count outstanding receivables (issued/partial with a
// positive balance), bucketed by expected payment date. Mirror that here so a
// period chip filters to exactly its rows; anything else returns null.
function invoiceDuePeriod(i: Invoice, today: string): DuePeriod | null {
  if (i.status === 'paid' || i.status === 'cancelled') return null
  if (i.totalAmount - i.paidAmount <= 0) return null
  return classifyDuePeriod(i.expectedPaymentDate, today)
}

const INVOICE_BUCKET_LABELS: Record<number, string> = {
  7: '≤ 7 дн',
  14: '≤ 14 дн',
  30: '≤ 30 дн',
}

function ReceivableBanner({
  summary,
  scopedToOrg,
  activePeriod,
  onPeriodToggle,
}: {
  summary: InvoiceReceivableSummary
  scopedToOrg: boolean
  activePeriod: DuePeriod | null
  onPeriodToggle: (period: DuePeriod) => void
}) {
  // Total clients owe us — primary in UAH, plus any foreign-currency portion.
  const fxParts = (Object.entries(summary.byCurrency) as [Currency, number][])
    .filter(([cur, amount]) => cur !== 'UAH' && amount > 0)
    .map(([cur, amount]) => fmtMoney(amount, cur))
  return (
    <SummaryBanner
      icon="wallet"
      title={`Нам винні клієнти${scopedToOrg ? ' (організація)' : ''}`}
      totals={[
        { label: 'Загалом', value: fmtMoney(summary.receivableUah, 'UAH') },
        ...(fxParts.length
          ? [{ label: 'у т.ч. валютою', value: fxParts.join(' · '), tone: 'var(--accent)' }]
          : []),
      ]}
      countText={`${summary.count} ${pluralInvoice(summary.count)} в очікуванні`}
      fxIncomplete={summary.fxIncomplete}
      periodsLabel="Коли заплатять"
      overdueUah={summary.overdueUah}
      overdueCount={summary.overdueCount}
      buckets={summary.buckets}
      bucketLabels={INVOICE_BUCKET_LABELS}
      laterLabel="пізніше"
      laterUah={summary.laterUah}
      activePeriod={activePeriod}
      onPeriodToggle={onPeriodToggle}
    />
  )
}

// Ukrainian plural for "рахунок": 1 / 2-4 / 5+ (ignoring the 11-14 teens edge).
function pluralInvoice(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return 'рахунків'
  if (mod10 === 1) return 'рахунок'
  if (mod10 >= 2 && mod10 <= 4) return 'рахунки'
  return 'рахунків'
}

function isOverdue(inv: Invoice, today: string): boolean {
  return (
    (inv.status === 'issued' || inv.status === 'partial') &&
    inv.expectedPaymentDate != null &&
    inv.expectedPaymentDate < today
  )
}

function ProgressMini({
  value,
  total,
  currency,
  pct,
}: {
  value: number
  total: number
  currency: 'UAH' | 'USD' | 'EUR'
  pct: number
}) {
  return (
    <div>
      <div
        style={{
          height: 4,
          background: 'var(--bg-3)',
          borderRadius: 2,
          overflow: 'hidden',
          marginBottom: 2,
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: pct >= 100 ? 'var(--success-fg)' : 'var(--accent)',
          }}
        />
      </div>
      <div className="num" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
        {fmtMoney(value, currency)} / {fmtMoney(total, currency)}
      </div>
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="card" style={{ padding: 20 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 36,
            background: 'var(--bg-3)',
            borderRadius: 4,
            marginBottom: 6,
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  )
}

function EmptyStateLocal({
  isAdmin,
  isFiltered,
  onSync,
  onReset,
  bootstrapPending,
}: {
  isAdmin: boolean
  isFiltered: boolean
  onSync: () => void
  onReset: () => void
  bootstrapPending: boolean
}) {
  if (isFiltered) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ color: 'var(--fg-2)', marginBottom: 12 }}>
          Рахунків з такими фільтрами не знайдено.
        </div>
        <Button variant="secondary" size="sm" onClick={onReset}>
          Скинути фільтри
        </Button>
      </div>
    )
  }
  return (
    <div className="card" style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ color: 'var(--fg-2)', marginBottom: 12 }}>
        Рахунки з&apos;являться після першої синхронізації з Bitrix24.
      </div>
      {isAdmin && (
        <Button
          variant="primary"
          size="sm"
          icon="refresh-cw"
          onClick={onSync}
          disabled={bootstrapPending}
        >
          {bootstrapPending ? 'Синхронізація…' : 'Синхронізувати'}
        </Button>
      )}
    </div>
  )
}
